import { useEffect, useRef, useState } from "react";
import api from "../../api/account";
import { AuthUser, LicenseStatus } from "../../auth/types";
import DeleteAccountCard from "./DeleteAccountCard";
import EmailPreferencesCard from "./EmailPreferencesCard";
import SupportTicket from "./SupportTicket";

const statusColors: Record<LicenseStatus, { bg: string; color: string }> = {
  Active: { bg: "#e6f9ee", color: "#1a7a3c" },
  Expired: { bg: "#ffe5e5", color: "#b30000" },
  Unknown: { bg: "#f0f0f5", color: "#6e6e73" },
};

const DeviceIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    width="20"
    height="20"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

type AccountDashboardProps = {
  user: AuthUser;
  onSignOut: () => void;
};

export default function AccountDashboard({
  user,
  onSignOut,
}: AccountDashboardProps) {
  const status = statusColors[user.licenseStatus] || statusColors.Unknown;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    api.account
      .getAvatarImage(user.email, user)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setAvatarUrl(url);
      })
      .catch(() => setAvatarUrl(null));

    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [user]);

  return (
    <div className="account-page">
      {/* Header */}
      <div className="account-header">
        <div className="account-avatar">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={user.name}
              className="account-avatar-img"
            />
          ) : (
            user.avatar
          )}
        </div>
        <div className="account-header-info">
          <h1 className="account-name">{user.name}</h1>
          <p className="account-email">{user.email}</p>
        </div>
        <button className="account-signout-btn" onClick={onSignOut}>
          Sign Out
        </button>
      </div>

      <div className="account-grid">
        {/* Subscription */}
        <div className="account-card account-card--wide">
          <h2 className="account-card-title">Subscription</h2>
          <div className="account-stat-row">
            <div className="account-stat">
              <span className="account-stat-label">Status</span>
              <span
                className="account-license-badge"
                style={{ background: status.bg, color: status.color }}
              >
                {user.licenseStatus}
              </span>
            </div>
            <div className="account-stat">
              <span className="account-stat-label">Registered</span>
              <span className="account-stat-value">{user.registeredDate}</span>
            </div>
            <div className="account-stat">
              <span className="account-stat-label">Purchase Date</span>
              <span className="account-stat-value">{user.purchaseDate}</span>
            </div>
            <div className="account-stat">
              <span className="account-stat-label">Expiry Date</span>
              <span className="account-stat-value">{user.expiryDate}</span>
            </div>
            <div className="account-stat">
              <span className="account-stat-label">Last Login</span>
              <span className="account-stat-value">{user.lastLogin}</span>
            </div>
          </div>
        </div>

        {/* Devices */}
        <div className="account-card">
          <h2 className="account-card-title">Devices</h2>
          <div className="account-device-big">
            <DeviceIcon />
            <span className="account-device-big-num">{user.devicesUsed}</span>
          </div>
          <p className="account-device-label">
            {user.devicesUsed === 1 ? "device registered" : "devices registered"}
          </p>
          <div className="account-device-bar-track" style={{ marginTop: 14 }}>
            <div
              className="account-device-bar-fill"
              style={{
                width: `${Math.min(100, (user.devicesUsed / 10) * 100)}%`,
              }}
            />
          </div>
        </div>

        {/* License */}
        <div className="account-card">
          <h2 className="account-card-title">License</h2>
          <ul className="account-meta-list">
            <li>
              <span className="account-meta-key">License ID</span>
              <span className="account-meta-val account-meta-val--mono">
                {user.licenseId}
              </span>
            </li>
            <li>
              <span className="account-meta-key">Android version</span>
              <span className="account-meta-val">
                {user.rawMeta?.["and-version"] ?? "—"}
              </span>
            </li>
            <li>
              <span className="account-meta-key">iOS version</span>
              <span className="account-meta-val">
                {user.rawMeta?.["ios-version"] ?? "—"}
              </span>
            </li>
          </ul>
        </div>

        {/* Support ticket */}
        <SupportTicket user={user} />

        {/* Email preferences */}
        <EmailPreferencesCard user={user} />

        {/* Delete Account */}
        <DeleteAccountCard user={user} onSignOut={onSignOut} />
      </div>
    </div>
  );
}
